import React from "react";
import { MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import {
  BR_FUNCTION_OPTIONS,
  type TargetFormValues,
  type TeachingProcedure,
} from "../../TargetBuilderPanel.types";

interface BehaviorReductionFieldsProps {
  replacementBehaviorTargetOptions?: Array<{ id: number; title: string }>;
}

const BehaviorReductionFields: React.FC<BehaviorReductionFieldsProps> = ({
  replacementBehaviorTargetOptions = [],
}) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const procedure = values.teaching_procedure;
  if (!procedure) return null;

  const update = (partial: Partial<TeachingProcedure>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  return (
    <Stack gap={5}>
      <FieldGroup label="Date of Implementation" required>
        <TextField
          size="small"
          type="date"
          value={procedure.date_of_implementation ?? ""}
          onChange={(e) => update({ date_of_implementation: e.target.value })}
          inputProps={{ "aria-label": "Date of implementation" }}
          InputLabelProps={{ shrink: true }}
        />
      </FieldGroup>

      <FieldGroup label="Operational Definition" required>
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.operational_definition ?? ""}
          onChange={(e) => update({ operational_definition: e.target.value })}
          placeholder="Define the behavior in observable and measurable terms"
          aria-label="Behavior reduction operational definition"
        />
      </FieldGroup>

      <FieldGroup label="Function" required>
        <Select
          size="small"
          fullWidth
          value={procedure.function ?? ""}
          onChange={(e) =>
            update({
              function: e.target.value as NonNullable<TeachingProcedure["function"]>,
            })
          }
          displayEmpty
          aria-label="Behavior function"
          renderValue={(selected) =>
            selected ? (
              BR_FUNCTION_OPTIONS.find((opt) => opt.value === selected)?.label
            ) : (
              <Typography variant="body2" color="text.disabled">
                Select function
              </Typography>
            )
          }
        >
          {BR_FUNCTION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup label="Assessment Used" required>
        <TextField
          fullWidth
          value={procedure.assessment_used ?? ""}
          onChange={(e) => update({ assessment_used: e.target.value })}
          placeholder="e.g., FBA interview + ABC observation"
          aria-label="Assessment used"
        />
      </FieldGroup>

      <FieldGroup label="Common Antecedents" required>
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.common_antecedents ?? ""}
          onChange={(e) => update({ common_antecedents: e.target.value })}
          placeholder="Describe common antecedent events"
          aria-label="Common antecedents"
        />
      </FieldGroup>

      <FieldGroup label="Antecedent Strategies">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.antecedent_strategies ?? ""}
          onChange={(e) => update({ antecedent_strategies: e.target.value })}
          placeholder="Describe prevention and antecedent interventions"
          aria-label="Antecedent strategies"
        />
      </FieldGroup>

      <FieldGroup label="Linked Replacement Behavior">
        <Select
          size="small"
          fullWidth
          value={procedure.linked_replacement_behavior ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            update({
              linked_replacement_behavior: raw === "" ? "" : Number(raw),
            });
          }}
          displayEmpty
          aria-label="Linked replacement behavior"
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography variant="body2" color="text.disabled">
                  Select target (optional)
                </Typography>
              );
            }
            const id = Number(selected);
            return (
              replacementBehaviorTargetOptions.find((opt) => opt.id === id)?.title ??
              String(selected)
            );
          }}
        >
          <MenuItem value="">None</MenuItem>
          {replacementBehaviorTargetOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.title}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup label="Safety Considerations">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.safety_considerations ?? ""}
          onChange={(e) => update({ safety_considerations: e.target.value })}
          placeholder="Document safety risks, escalation plan, and staffing notes"
          aria-label="Safety considerations"
        />
      </FieldGroup>
    </Stack>
  );
};

export default BehaviorReductionFields;
